import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ResizableHeader from './ResizableHeader';
import { MIN_COLUMN_WIDTH, DEFAULT_COLUMN_WIDTH, RESIZE_SORT_SUPPRESS_MS } from './constants';
import { parseColumnWidth, getColumnKey } from './utils';

/**
 * Hook: resizable columns (client-side only).
 * Requires enableResize on the table and resizable: true on each column that should resize.
 */
export function useResizableColumns(columns, onSort, enableResize = false) {
    const [columnWidths, setColumnWidths] = useState({});
    const resizeRef = useRef({ key: null, startX: 0, startWidth: 0 });
    const resizeJustFinishedRef = useRef(false);

    const handleResizeStart = useCallback((columnKey, clientX, currentWidth) => {
        resizeRef.current = { key: columnKey, startX: clientX, startWidth: currentWidth };
    }, []);

    useEffect(() => {
        if (!enableResize) return undefined;

        const onMouseMove = (e) => {
            const { key, startX, startWidth } = resizeRef.current;
            if (key == null) return;
            const next = Math.max(MIN_COLUMN_WIDTH, startWidth + (e.clientX - startX));
            setColumnWidths((prev) => ({ ...prev, [key]: next }));
        };
        const onMouseUp = () => {
            const wasResizing = resizeRef.current.key != null;
            resizeRef.current = { key: null, startX: 0, startWidth: 0 };
            if (wasResizing) {
                resizeJustFinishedRef.current = true;
                setTimeout(() => {
                    resizeJustFinishedRef.current = false;
                }, RESIZE_SORT_SUPPRESS_MS);
            }
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }, [enableResize]);

    const enhancedColumns = useMemo(() => {
        if (!enableResize) return columns;

        return columns.map((col, index) => {
            if (!col.resizable) return col;

            const { resizable, ...colWithoutFlag } = col;
            const key = getColumnKey(col, index);
            const baseWidth =
                parseColumnWidth(col.width ?? col.minWidth ?? col.maxWidth) ?? DEFAULT_COLUMN_WIDTH;
            const widthPx = columnWidths[key] ?? baseWidth;
            const widthStr = `${widthPx}px`;

            return {
                ...colWithoutFlag,
                name: (
                    <ResizableHeader
                        columnName={col.name}
                        resizeKey={key}
                        displayWidth={widthPx}
                        onResizeStart={handleResizeStart}
                    />
                ),
                width: widthStr,
                minWidth: widthStr,
                ...(col.maxWidth && { maxWidth: col.maxWidth }),
            };
        });
    }, [columns, columnWidths, handleResizeStart, enableResize]);

    const handleSort = useCallback(
        (column, sortDirection) => {
            if (enableResize && resizeJustFinishedRef.current) return;
            onSort?.(column, sortDirection);
        },
        [onSort, enableResize]
    );

    return { enhancedColumns, handleSort };
}
